import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import {
  getUserSettingsCulture,
  getUserSettingsLanguage,
  getUserSettingsTimezone
} from '@capital-access/common/globalization';
import { LocalizationService } from '@capital-access/common/localization';
import {
  getSelectedSecurity,
  getSelectedSecurityCompanyName,
  getSelectedSecuritySurveillancePermission
} from '@capital-access/common/security';
import { createHistoricalOwnershipReportFinished } from '@capital-access/reporting/common';
import { LOCALIZATION_KEY } from '../../constants';
import { generateReport } from '../../core/+state/report.actions';
import { OwnershipSource } from '../../core/models/ownership-source.enum';
import { FileNameService } from '../../core/services/file-name.service';
import { createHistoricalOwnershipReport } from '../+state/historical-ownership-report.actions';
import { CreateHistoricalOwnershipRequest } from '../models/historical-ownership-report.models';

@Injectable()
export class HistoricalOwnershipReportEffects {
  createHistoricalOwnershipReportRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createHistoricalOwnershipReport),
      withLatestFrom(
        this.store.select(getSelectedSecurity),
        this.store.select(getSelectedSecurityCompanyName),
        this.store.select(getUserSettingsTimezone),
        this.store.select(getSelectedSecuritySurveillancePermission),
        this.store.select(getFeatureToggle('skyl-2231-culture-specific-formatting')),
        this.store.select(getUserSettingsCulture),
        this.store.select(getUserSettingsLanguage),
        this.localization.localize('historicalOwnershipDefaultFilename', {}, { scope: LOCALIZATION_KEY })
      ),
      mergeMap(
        ([
          { sections, includeTables, reportedQuarters, filters, sortDirection, displayCurrentBreakdown },
          security,
          companyName,
          timeZone,
          isSrvPermissioned,
          cultureFtEnabled,
          culture,
          language,
          historicalOwnershipDefaultFilename
        ]) => {
          const isSrv = security?.surveillance ?? false;
          return this.fileNameService
            .getDefaultFileName(isSrvPermissioned, isSrv, companyName ?? historicalOwnershipDefaultFilename, timeZone)
            .pipe(
              map(fileName => {
                const createReportRequest: CreateHistoricalOwnershipRequest = {
                  sections,
                  timeZone,
                  securityId: security?.id,
                  fileName,
                  culture: cultureFtEnabled ? culture : 'en-US',
                  language: language,
                  includeTables,
                  reportedQuarters,
                  filters,
                  sortDirection,
                  displayCurrentBreakdown,
                  dataSource: isSrv ? OwnershipSource.Surveillance : OwnershipSource.Public
                };

                return generateReport({
                  request: createReportRequest,
                  url: 'historical-ownership-reports/process/launch',
                  successMessage: 'historicalOwnershipReportCreated',
                  failureMessage: 'historicalOwnershipReportFailed',
                  finishAction: createHistoricalOwnershipReportFinished()
                });
              })
            );
        }
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private fileNameService: FileNameService,
    private localization: LocalizationService
  ) {}
}
